/**
 * Write auditing for Super Admin Support Mode (editing enabled).
 *
 * Installed ONLY while an editing support session is active and removed the
 * moment the session ends or reverts, so normal users never execute this code.
 *
 * It never blocks or alters a request — it observes the table/operation and
 * appends an entry to `support_session_events` (the session row already carries
 * the Super Admin user, the target company and the original identity).
 *
 * Row Level Security remains the final authority and is not modified here.
 */
import { supabase } from "@/integrations/supabase/client";
import { getSupportSession } from "@/lib/supportSession";

const WRITE_METHODS = new Set(["insert", "update", "upsert", "delete"]);
const STORAGE_WRITE_METHODS = new Set(["upload", "uploadToSignedUrl", "update", "remove", "move", "copy"]);

/** The audit trail must never audit itself. */
const AUDIT_TABLES = new Set(["support_sessions", "support_session_events"]);

let installed = false;
let originals: { from: any; storageFrom: any; invoke: any } | null = null;

function record(resource: string, operation: string, extra?: Record<string, unknown>) {
  const active = getSupportSession();
  if (!active) return;
  void (supabase.from("support_session_events") as any)
    .insert({
      session_id: active.id,
      event_type: "action",
      path: typeof window !== "undefined" ? window.location.pathname : null,
      description: `${operation.toUpperCase()} ${resource}`,
      metadata: {
        resource,
        operation,
        access: "write",
        company_id: active.companyId,
        ...(extra ?? {}),
      },
    })
    .then(() => undefined, () => undefined);
}

export function installSupportWriteAudit() {
  if (installed) return;
  const client: any = supabase;

  originals = {
    from: client.from.bind(client),
    storageFrom: client.storage.from.bind(client.storage),
    invoke: client.functions.invoke.bind(client.functions),
  };

  client.from = (table: string) => {
    const builder = originals!.from(table);
    if (AUDIT_TABLES.has(table)) return builder;
    return new Proxy(builder, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof prop === "string" && WRITE_METHODS.has(prop) && typeof value === "function") {
          return (...args: unknown[]) => {
            record(table, prop);
            return (value as any).apply(target, args);
          };
        }
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  };

  client.storage.from = (bucket: string) => {
    const api = originals!.storageFrom(bucket);
    return new Proxy(api, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof prop === "string" && STORAGE_WRITE_METHODS.has(prop) && typeof value === "function") {
          return (...args: unknown[]) => {
            record(`storage:${bucket}`, prop);
            return (value as any).apply(target, args);
          };
        }
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  };

  client.functions.invoke = (name: string, options?: any) => {
    record(`function:${name}`, "invoke");
    return originals!.invoke(name, options);
  };

  installed = true;
}

export function removeSupportWriteAudit() {
  if (!installed || !originals) return;
  const client: any = supabase;
  client.from = originals.from;
  client.storage.from = originals.storageFrom;
  client.functions.invoke = originals.invoke;
  originals = null;
  installed = false;
}
